<template>
    <div class="bookingtable-wrapper" v-if="locations.length > 0 && experiences.length > 0">
        <booking-filters :locations="locations" :experiences="experiences" :stats="statsData" />
        <div v-if="!loading" class="bookingtable-container">
            <div class="table-tabs">
                <button :class="`tab next-bookings ${!filterStore.past.value ? 'selected' : ''
                    }`" @click="() => selectTable(false)">
                    Upcoming Bookings ({{ nextBookings.length }})
                </button>
                <button :class="`tab past-bookings ${filterStore.past.value ? 'selected' : ''
                    }`" @click="() => selectTable(true)">
                    Past Bookings ({{ lastBookings.length }})
                </button>
            </div>
            <div class="tables">
                <booking-table :class="`booking-table ${!filterStore.past.value ? 'selected' : ''
                    }`" :bookings="nextBookings" />
                <booking-table :class="`booking-table ${filterStore.past.value ? 'selected' : ''
                    }`" :bookings="lastBookings" />
            </div>
        </div>
        <spinner :loading="loading" text="Loading bookings..."></spinner>
        <download-csv-bar :locations="locations" :experiences="experiences" />
    </div>
</template>

<script setup>
import { defineProps, ref, watch } from "vue";
import { getBookingTable } from "../../api/rest";

import { useUrlFilters } from "../../composables/useUrlFilters";
import Spinner from "../shared/Spinner.vue";
import DownloadCsvBar from "./DownloadCsvBar.vue";
import BookingFilters from "./Filters.vue";
import BookingTable from "./Table.vue";

const props = defineProps({
    locations: {
        type: Array,
        default: () => [],
    },
    experiences: {
        type: Array,
        default: () => [],
    },
});

const filterStore = useUrlFilters(
    () => props.locations,
    () => props.experiences,
);

const { filters } = filterStore;

const nextBookings = ref([]);
const lastBookings = ref([]);
const statsData = ref({});
const loading = ref(false);

const selectTable = (past) => {
    filterStore.past.value = past;
};

const filterBookings = async () => {
    if (!filters.value) {
        return;
    }
    try {
        loading.value = true;
        const { bookings, stats } = await getBookingTable(filters.value);
        statsData.value = stats;
        const result = bookings.map((booking) => {
            const options = [];
            if (booking.refund_requested) {
                options.push("Refund");
            }
            if (booking.voucher_requested) {
                options.push("Voucher");
            }
            return { ...booking, options };
        })

        lastBookings.value = result.filter((booking) => new Date(booking.date) < new Date()).reverse();
        nextBookings.value = result
            .filter((booking) => new Date(booking.date) > new Date());
        loading.value = false;
    } catch (e) {
        loading.value = false;
        alert(e.message);
    } finally {
        loading.value = false;
    }
};

watch(filters, () => {
    filterBookings();
}, { immediate: true });

// Initial load
filterBookings();
</script>

<style scoped lang="scss">
@use "../../colors" as *;

.bookingtable-wrapper {
    position: relative;
    width: 100%;
    min-width: 0;
}

.bookingtable-container {
    display: flex;
    flex-direction: column;
    padding-top: 1rem;
}

.table-tabs {
    display: flex;
    justify-content: space-between;

    .tab {
        width: 50%;

        background-color: rgba($color-background, 0.71);
        color: $color-primary;
        border: 1px solid $color-primary;
        border-radius: 0;
        padding: 0.5rem 0.75rem;
        font: inherit;
        font-size: 0.9rem;

        &.selected {
            background-color: $color-primary;
            color: $color-background;
        }

        &:nth-child(1) {
            border-radius: 0.5rem 0 0 0;
        }

        &:nth-child(2) {
            border-radius: 0 0.5rem 0 0;
        }
    }
}

.tables {
    height: 100%;
    display: flex;
    flex-direction: column;
    font-size: 1rem;

    .booking-table {
        // flex: 1 1 320px;

        border: 1px solid $color-primary;
        border-top: 0;
        overflow: hidden;

        &:not(.selected) {
            display: none;
        }

        &:nth-child(1) {
            border-radius: 0 0 0 0.5rem;
        }

        &:nth-child(2) {
            border-radius: 0 0 0.5rem 0;
        }
    }
}

.page {
    width: 100%;
}

.spinner {
    padding: 4rem;
    display: flex;
    flex-direction: column;
}

@media only screen and (max-width: 768px) {
    .table-tabs {
        font-size: 0.9rem;
    }
}

@media only screen and (max-width: 768px),
(min-device-width: 1024px) and (max-device-width: 1440px) {
    .table-tabs {
        &.active {
            display: flex;

            .selected {
                background-color: #ffffff30;
            }
        }

        &.inactive {
            display: none;
        }
    }

    .tables [data-tab] {
        display: none;

        &.selected {
            display: block;
        }
    }
}

</style>